import { Card, CardContent } from '@/components/ui/card';
import { BidHistoryStats } from '@/types/bidHistory';
import { 
  FileText, 
  Trophy, 
  Percent, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Clock,
  BarChart3
} from 'lucide-react';

interface BidHistoryKPICardsProps {
  stats: BidHistoryStats;
  loading?: boolean;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatHours(hours: number): string {
  if (hours < 24) {
    return `${Math.round(hours)}h`;
  }
  const days = Math.round(hours / 24);
  return `${days}d`;
}

interface KPICardProps {
  title: string;
  value: string;
  change?: number;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  loading?: boolean;
}

function KPICard({ title, value, change, icon: Icon, iconColor, loading }: KPICardProps) {
  const showTrend = change !== undefined && change !== 0;
  const isPositive = (change || 0) >= 0;

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-4">
        {loading ? (
          <div className="space-y-2">
            <div className="h-4 w-20 bg-muted animate-pulse rounded" />
            <div className="h-8 w-16 bg-muted animate-pulse rounded" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{title}</span>
              <Icon className={`h-4 w-4 ${iconColor}`} />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-bold">{value}</span>
              {showTrend && (
                <span className={`flex items-center text-xs font-medium ${
                  isPositive ? 'text-green-600' : 'text-red-600'
                }`}>
                  {isPositive ? (
                    <TrendingUp className="h-3 w-3 mr-0.5" />
                  ) : (
                    <TrendingDown className="h-3 w-3 mr-0.5" />
                  )}
                  {Math.abs(change).toFixed(1)}%
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function BidHistoryKPICards({ stats, loading }: BidHistoryKPICardsProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <KPICard
        title="Total Bids"
        value={stats.totalBids.toString()}
        change={stats.trends.totalBidsChange}
        icon={FileText}
        iconColor="text-blue-500"
        loading={loading}
      />
      <KPICard
        title="Won Bids"
        value={stats.wonBids.toString()}
        change={stats.trends.wonBidsChange}
        icon={Trophy}
        iconColor="text-green-500"
        loading={loading}
      />
      <KPICard
        title="Win Rate"
        value={`${stats.winRate.toFixed(1)}%`}
        change={stats.trends.winRateChange}
        icon={Percent}
        iconColor="text-purple-500"
        loading={loading}
      />
      <KPICard
        title="Total Won"
        value={formatCurrency(stats.totalWonValueCents)}
        change={stats.trends.totalWonValueChange}
        icon={DollarSign}
        iconColor="text-emerald-500"
        loading={loading}
      />
      <KPICard
        title="Avg Bid"
        value={formatCurrency(stats.avgBidValueCents)}
        icon={BarChart3}
        iconColor="text-orange-500"
        loading={loading}
      />
      <KPICard
        title="Avg Response"
        value={formatHours(stats.avgResponseTimeHours)}
        icon={Clock}
        iconColor="text-cyan-500"
        loading={loading}
      />
    </div>
  );
}
